import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * CR-001 §50–§53 — the "Unlinked Invoices" and "Unlinked Payments" screens.
 *
 * These rows could not satisfy the new rules and §52 forbids inventing a client
 * to hide that, so the migration set them aside instead of deleting them. Here
 * somebody who knows the business says where each one belongs, and it is
 * restored with its original number, dates and amounts intact.
 */
export function DataMigrationPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const status = useQuery({
    queryKey: ['migration-status'],
    queryFn: () => api.get<any>('/finance/migration/status'),
  });
  const invoices = useQuery({
    queryKey: ['migration-invoices'],
    queryFn: () => api.get<any[]>('/finance/migration/unlinked-invoices'),
  });
  const payments = useQuery({
    queryKey: ['migration-payments'],
    queryFn: () => api.get<any[]>('/finance/migration/unlinked-payments'),
  });

  if (status.isLoading) return <LoadingBlock />;
  if (status.error) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;

  return (
    <>
      <PageHeader title={t('migration.title')} subtitle={t('migration.subtitle')} />

      {status.data?.complete ? (
        <Card>
          <EmptyState
            icon={<CheckCircle2 className="h-8 w-8 text-success-fg dark:text-success" />}
            title={t('migration.complete')}
            hint={t('migration.completeHint')}
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <Metric
              label={t('migration.unlinkedInvoices')}
              value={status.data.unlinkedInvoices}
              tone={status.data.unlinkedInvoices > 0 ? 'warning' : 'success'}
              icon={<ShieldAlert className="h-4 w-4" />}
            />
            <Metric
              label={t('migration.unlinkedPayments')}
              value={status.data.unlinkedPayments}
              tone={status.data.unlinkedPayments > 0 ? 'warning' : 'success'}
              icon={<ShieldAlert className="h-4 w-4" />}
            />
          </div>

          <Card className="mb-5">
            <p className="text-body-sm text-muted">{t('migration.noFakeData')}</p>
          </Card>

          <Card className="mb-5" padded={false}>
            <CardHeader title={t('migration.unlinkedInvoices')} className="p-5 pb-0" />
            {invoices.isLoading ? (
              <LoadingBlock />
            ) : !invoices.data?.length ? (
              <EmptyState title={t('migration.complete')} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('tickets.number')}</Th>
                    <Th>{t('invoices.counterparty')}</Th>
                    <Th>{t('invoices.issueDate')}</Th>
                    <Th align="end">{t('common.total')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('migration.reason')}</Th>
                    <Th align="end">{t('common.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.data.map((row) => (
                    <Tr key={row.id}>
                      <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{row.number}</span></Td>
                      <Td>{row.counterpartyName}</Td>
                      <Td className="tabular text-muted">{formatDate(row.issueDate, locale)}</Td>
                      <Td align="end" className="tabular">{formatMoney(row.totalAmount, row.currency, locale)}</Td>
                      <Td><Badge tone="neutral">{row.status}</Badge></Td>
                      <Td className="max-w-sm text-caption text-muted">{row.reason}</Td>
                      <Td align="end">
                        <span className="flex justify-end gap-2">
                          <Button variant="primary" onClick={() => navigate(`/finance/migration/invoices/${row.id}/restore`)}>
                            {t('migration.restore')}
                          </Button>
                          <Button variant="danger" onClick={() => navigate(`/finance/migration/invoices/${row.id}/discard`)}>
                            {t('migration.discard')}
                          </Button>
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('migration.unlinkedPayments')} className="p-5 pb-0" />
            {payments.isLoading ? (
              <LoadingBlock />
            ) : !payments.data?.length ? (
              <EmptyState title={t('migration.complete')} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.date')}</Th>
                    <Th>{t('migration.originalInvoice')}</Th>
                    <Th>{t('payments.method')}</Th>
                    <Th align="end">{t('common.amount')}</Th>
                    <Th>{t('migration.reason')}</Th>
                    <Th align="end">{t('common.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {payments.data.map((row) => (
                    <Tr key={row.id}>
                      <Td className="tabular text-muted">{formatDate(row.paymentDate, locale)}</Td>
                      <Td>
                        {row.originalInvoiceNumber ? (
                          <span className="font-mono text-mono-sm">{row.originalInvoiceNumber}</span>
                        ) : (
                          <span className="text-faint">—</span>
                        )}
                      </Td>
                      <Td className="text-muted">{row.paymentMethod.replace('_', ' ')}</Td>
                      <Td align="end" className="tabular">{formatMoney(row.amount, row.currency, locale)}</Td>
                      <Td className="max-w-sm text-caption text-muted">{row.reason}</Td>
                      <Td align="end">
                        <span className="flex justify-end gap-2">
                          <Button variant="primary" onClick={() => navigate(`/finance/migration/payments/${row.id}/restore`)}>
                            {t('migration.restore')}
                          </Button>
                          <Button variant="danger" onClick={() => navigate(`/finance/migration/payments/${row.id}/discard`)}>
                            {t('migration.discard')}
                          </Button>
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}

    </>
  );
}
